"use client";

import type React from "react";

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Package,
  Box,
  DollarSign,
  CreditCard,
  Banknote,
  Wallet,
  Trash2,
  PlusCircle,
  CalendarIcon,
  ClipboardListIcon,
  Edit,
  ClipboardIcon,
  ArrowLeft,
} from "lucide-react";
import type { FastOrderOutput } from "@/interfaces/fastOrders";
import {
  orderPaymentDetailsMethodTranslations,
  PaymentDetailsMethod,
  type PaymentsInput,
} from "@/interfaces/payments";
import { FastOrderService } from "@/services/FastOrderService";
import { PaymentsService } from "@/services/PaymentService";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import CustomCombobox from "@/components/CustomCombobox";
import { format } from "date-fns";
import CurrencyInput from "@/components/CurrencyInput";
import { DatePickerComponent } from "@/components/DatePickerComponent";
import { localDate } from "@/util/dates";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { numberToMoney } from "@/util/moneyConversion";

interface InfoItemProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  valueClassName?: string;
}

function InfoItem({ icon, title, value, valueClassName = "" }: InfoItemProps) {
  return (
    <div className="bg-gray-50 p-3 rounded-lg">
      <div className="flex items-center gap-2 mb-0.5">
        {icon}
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      </div>
      <p className={`text-sm font-medium ${valueClassName}`}>{value}</p>
    </div>
  );
}

export const FastOrderDetails = () => {
  const [fastOrder, setFastOrder] = useState<FastOrderOutput | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [newPayment, setNewPayment] = useState<Partial<PaymentsInput>>({
    orderId: id,
    method: PaymentDetailsMethod.CASH,
    amount: 0,
    paymentDate: localDate(),
  });
  const [loading, setLoading] = useState<boolean>(true);
  const service = new FastOrderService();
  const paymentsService = new PaymentsService();

  // Usando o hook para gerenciar o estado de exclusão
  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  useEffect(() => {
    fetchFastOrder();
  }, [id]);

  const fetchFastOrder = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await service.findById(id);
      setFastOrder(data);
    } catch (error) {
      console.error("Erro ao buscar pedido rápido:", error);
      navigate("/pedidos-rapidos");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    if (newPayment.amount && newPayment.amount > 0 && newPayment.method) {
      try {
        setLoading(true);
        await paymentsService.create(newPayment as PaymentsInput);
        setIsModalOpen(false);
        await fetchFastOrder();
      } catch (error) {
        console.error("Erro ao adicionar pagamento:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeletePayment = (paymentId: string) => {
    openDeleteDialog(paymentId); // Abre o dialog de confirmação
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        setLoading(true);
        await paymentsService.delete(itemToDelete);
        await fetchFastOrder();
      } catch (error) {
        console.error("Erro ao excluir pagamento:", error);
      } finally {
        setLoading(false);
      }
    }
    closeDeleteDialog(); // Fecha o dialog após a exclusão
  };

  const getPaymentMethodIcon = (method: PaymentDetailsMethod) => {
    switch (method) {
      case PaymentDetailsMethod.CREDIT_CARD:
        return <CreditCard className="w-4 h-4 text-[#EE7B11]" />;
      case PaymentDetailsMethod.CASH:
        return <Banknote className="w-4 h-4 text-[#EE7B11]" />;
      default:
        return <Wallet className="w-4 h-4 text-[#EE7B11]" />;
    }
  };

  const paymentMethodOptions = Object.entries(
    orderPaymentDetailsMethodTranslations
  ).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!fastOrder) {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <Card className="overflow-hidden border-none shadow-md">
          <CardContent className="p-8 text-center">
            <div className="text-center py-8">
              <ClipboardListIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Pedido rápido não encontrado
              </h3>
              <p className="text-gray-500 mb-6">
                Não foi possível encontrar os dados deste pedido.
              </p>
              <Button
                onClick={() => navigate("/pedidos-rapidos")}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar para lista de pedidos
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPaid = (fastOrder.payments || []).reduce(
    (sum, payment) => sum + (payment.amount || 0),
    0
  );
  const remainingAmount = Math.max(0, (fastOrder.totalAmount || 0) - totalPaid);
  const isFullyPaid = remainingAmount <= 0;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <Button
          onClick={() => navigate("/pedidos-rapidos")}
          variant="outline"
          size="sm"
          className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <Button
          onClick={() => navigate(`/pedidos-rapidos/${fastOrder.id}/editar`)}
          variant="outline"
          size="sm"
          className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
        >
          <Edit className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </div>

      <Card className="overflow-hidden border-none shadow-md">
        <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white pb-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <ClipboardListIcon className="w-5 h-5" />
              Pedido Rápido #{fastOrder.id}
            </CardTitle>
            <Badge
              variant={isFullyPaid ? "success" : "destructive"}
              className="text-xs px-2 py-0.5 rounded-full"
            >
              {isFullyPaid ? "Pago" : "Pendente"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InfoItem
              icon={<CalendarIcon className="w-4 h-4 text-[#EE7B11]" />}
              title="Data do pedido"
              value={format(new Date(fastOrder.orderDate), "dd/MM/yyyy")}
            />
            <InfoItem
              icon={<DollarSign className="w-4 h-4 text-[#EE7B11]" />}
              title="Valor Total"
              value={numberToMoney(fastOrder.totalAmount || 0)}
              valueClassName="text-green-600"
            />
            <InfoItem
              icon={<DollarSign className="w-4 h-4 text-[#EE7B11]" />}
              title="Desconto"
              value={`-${numberToMoney(fastOrder.discount || 0)}`}
              valueClassName="text-red-600"
            />
          </div>

          {fastOrder.description && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-0.5">
                <ClipboardIcon className="w-4 h-4 text-[#EE7B11]" />
                <h3 className="text-sm font-medium text-gray-500">Descrição</h3>
              </div>
              <p className="text-sm">{fastOrder.description}</p>
            </div>
          )}

          <Separator className="my-4" />
          <div className="flex items-center gap-2 mb-4">
            <Box className="w-4 h-4 text-[#EE7B11]" />
            <h3 className="text-lg font-semibold">Produtos</h3>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="divide-y">
              {(fastOrder.products || []).map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 hover:bg-gray-50"
                >
                  <Package className="h-5 w-5 shrink-0 text-[#EE7B11] mt-1" />
                  <div className="min-w-0 flex-1">
                    <h4
                      className="font-medium truncate text-sm"
                      title={item.name}
                    >
                      {item.name}
                    </h4>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                      <span>
                        <span className="text-gray-500">Qtd:</span>{" "}
                        <span className="font-medium">{item.quantity}</span>
                      </span>
                      <span>
                        <span className="text-gray-500">Unit:</span>{" "}
                        <span className="font-medium">
                          {numberToMoney(item.unitPrice || 0)}
                        </span>
                      </span>
                      <span>
                        <span className="text-gray-500">Total:</span>{" "}
                        <span className="font-medium">
                          {numberToMoney(
                            (item.unitPrice || 0) * (item.quantity || 0)
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Separator className="my-4" />
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#EE7B11]" />
                Pagamentos
              </h3>
              <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Adicionar Pagamento
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Adicionar Pagamento</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="amount" className="text-right">
                        Valor
                      </Label>
                      <CurrencyInput
                        id="amount"
                        value={newPayment.amount || 0}
                        onValueChange={(value) =>
                          setNewPayment({
                            ...newPayment,
                            amount: value,
                          })
                        }
                        className="col-span-3 border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="method" className="text-right">
                        Método
                      </Label>
                      <CustomCombobox
                        value={newPayment.method}
                        onChange={(value) =>
                          setNewPayment({
                            ...newPayment,
                            method: value as PaymentDetailsMethod,
                          })
                        }
                        options={paymentMethodOptions}
                        className="col-span-3 border-gray-300 focus:border-[#EE7B11] focus:ring-[#EE7B11]"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="paymentDate" className="text-right">
                        Data
                      </Label>
                      <DatePickerComponent
                        id="paymentDate"
                        selected={newPayment.paymentDate ?? localDate()}
                        onSelect={(value) =>
                          setNewPayment({
                            ...newPayment,
                            paymentDate: value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleAddPayment}
                      className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
                    >
                      Adicionar
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {(fastOrder.payments || []).length > 0 ? (
              <div className="space-y-2">
                {(fastOrder.payments || []).map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      {getPaymentMethodIcon(payment.method)}
                      <div>
                        <p className="font-medium">
                          {
                            orderPaymentDetailsMethodTranslations[
                              payment.method
                            ]
                          }
                        </p>
                        <p className="text-xs text-gray-500">
                          {format(new Date(payment.paymentDate), "dd/MM/yyyy")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-semibold text-[#EE7B11]">
                        {numberToMoney(payment.amount || 0)}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePayment(payment.id)}
                        className="text-red-500 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 border rounded-lg bg-gray-50">
                <p className="text-gray-500">Nenhum pagamento registrado</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsModalOpen(true)}
                  className="mt-2 border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Adicionar Pagamento
                </Button>
              </div>
            )}

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 mb-1">Total Pago:</p>
                  <p className="font-semibold text-green-600 text-lg">
                    {numberToMoney(totalPaid)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Restante:</p>
                  <p
                    className={`font-semibold text-lg ${
                      isFullyPaid ? "text-green-600" : "text-red-500"
                    }`}
                  >
                    {numberToMoney(remainingAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Status:</p>
                  <Badge
                    variant={isFullyPaid ? "success" : "destructive"}
                    className="text-xs px-2 py-1"
                  >
                    {isFullyPaid ? "Pago" : "Pendente"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Componente de confirmação de exclusão */}
      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão de Pagamento"
        message="Tem certeza que deseja excluir este pagamento? Esta ação não pode ser desfeita."
        itemName={
          fastOrder.payments.find((p) => p.id === itemToDelete)?.amount != null
            ? `${
                orderPaymentDetailsMethodTranslations[
                  fastOrder.payments.find((p) => p.id === itemToDelete)
                    ?.method || PaymentDetailsMethod.CASH
                ]
              } - ${numberToMoney(
                fastOrder.payments.find((p) => p.id === itemToDelete)?.amount ||
                  0
              )}`
            : undefined
        }
      />
    </div>
  );
};

export default FastOrderDetails;
